import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MDBBox } from "mdbreact";
import { Popconfirm, message, Tooltip } from "antd";
import { setSidebarStatus } from "actions/user";
import {
  getWorkerHealth,
  getWorkerThroughputSummary,
  getWorkerOutboxStats
} from "api/worker/getHealth";
import {
  pauseQueue,
  resumeQueue,
  cleanQueue,
  cleanQueueByBrand,
  obliterateQueue,
  pauseScheduler,
  resumeScheduler,
  runSchedulerNow
} from "api/worker/control";
import "./WorkerHealth.scss";

const REFRESH_MS = 10000;

function StatusDot({ ok, warn }) {
  const color = ok ? "#28a745" : warn ? "#f59e0b" : "#dc3545";
  return <span className="wh-dot" style={{ backgroundColor: color }} />;
}

function StatTile({ label, value, sub }) {
  return (
    <div className="wh-tile">
      <div className="wh-tile-label">{label}</div>
      <div className="wh-tile-value">{value}</div>
      {sub != null && <div className="wh-tile-sub">{sub}</div>}
    </div>
  );
}

const fmtBytes = (n) => `${Math.round((n / 1024 / 1024 / 1024) * 10) / 10} GB`;
const fmtSecs = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
const fmtInterval = (ms) => {
  if (!ms) return "—";
  if (ms >= 3600000) return `${Math.round(ms / 3600000)}h`;
  if (ms >= 60000) return `${Math.round(ms / 60000)}m`;
  return `${Math.round(ms / 1000)}s`;
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString() : "—");

// Plain-English description for each queue. Keep these short and concrete —
// the dashboard's job is to tell an admin "what this is" and "what breaks
// if I pause it" at a glance.
const QUEUE_INFO = {
  "token-refresh-queue": {
    what: "Refreshes OAuth access tokens for connected social accounts before they expire.",
    stopped: "Tokens may expire. Posting will start failing with auth errors until manually reconnected."
  },
  "brand-processing-queue": {
    what: "One-shot processing for a brand: bootstrap settings, kick off the first idea round, etc.",
    stopped: "New brands won't auto-bootstrap; you'll need to trigger the first idea round manually."
  },
  "brand-enrichment-queue": {
    what: "Builds BrandSettings.brandProfile by pulling signals from connected social accounts (profile + top engagement posts), website HTML, and the brand/settings docs, then asks the LLM to synthesize a structured profile (style, tone, big idea, target customers, content pillars, top posts, etc.). Triggers on brand create, settings change, social connect, and on a 7-day staleness refresh.",
    stopped: "BrandProfile won't refresh. Idea + post generators fall back to the raw brand/settings fields without the deeper synthesis."
  },
  "idea-generation-queue": {
    what: "Calls the LLM (Claude/OpenAI) to draft N idea documents per brand. Triggered by the monthly planner or the daily fallback.",
    stopped: "No new ideas are generated. The Kanban Pending column stops filling. Already-approved ideas keep flowing."
  },
  "post-generation-queue": {
    what: "Turns an approved Idea into per-platform Post drafts (one Post per selected platform). Fans out to image/video generation if the brand wants media.",
    stopped: "Approved ideas pile up — no draft posts get created. Posting + media generation downstream go idle."
  },
  "article-generation-queue": {
    what: "Drafts long-form articles for brands that opt into article content.",
    stopped: "No new articles. Doesn't affect short-form posts."
  },
  "image-generation-queue": {
    what: "Calls Replicate (Flux) to render brand-styled images for posts that need media.",
    stopped: "Image-required posts stay stuck in 'generating-image'. Text-only and video posts still flow."
  },
  "video-generation-queue": {
    what: "Calls Replicate (Kling) to render short videos for posts that opt into video content.",
    stopped: "Video posts stay stuck in 'generating-video'. Image and text posts still flow."
  },
  "email-approval-queue": {
    what: "Sends the HMAC-signed approval email to the brand owner when a draft post is ready and autoApprove=false.",
    stopped: "Posts that need human approval never email out. Owners can still approve from the Kanban UI directly."
  },
  "social-posting-queue": {
    what: "The publisher. Calls the per-platform adapter (FB Graph, IG, X v2, LinkedIn, Pinterest, YouTube, TikTok, Reddit, Telegram, Discord) to actually post.",
    stopped: "Approved posts queue up but nothing reaches social media. duePostScheduler keeps marking posts due, but they sit in 'approved' until this resumes."
  },
  "engagement-refresh-queue": {
    what: "Pulls per-post engagement counts (likes/comments/shares) from each social platform's API; writes them onto Post.analytics and appends a PostAnalyticsSnapshot row for the engagement-over-time chart.",
    stopped: "Dashboards stop updating. Top-engaged-posts list goes stale. Auto-re-enrichment for high-engagement posts stops firing."
  },
  "comment-refresh-queue": {
    what: "Fetches actual comment bodies + commenter profiles for each posted Post (FB/IG/X/LinkedIn/TikTok/Pinterest/YouTube). Writes SocialComment + Commenter; emits COMMENT_SENTIMENT_REQUESTED for each post that got new rows.",
    stopped: "Post Analytics 'Comments' panel + 'Top engagers' table stop receiving new data. Sentiment classification stops downstream (no inputs)."
  },
  "comment-sentiment-queue": {
    what: "Claude Haiku 4.5 batch classifier: tags every fetched comment with sentiment (positive/neutral/negative) and intent (question/praise/complaint/sale-interest/other) in one call per post.",
    stopped: "Comments still arrive but sentiment badges + breakdown chart on Post Analytics go cold for new comments."
  },
  "email-ingestion-queue":     { what: "Inbound email pipeline phase 1: pull RFC822 from IMAP, store as EmailMessage, deduplicate.",                                                       stopped: "Inbound replies stop landing in the CRM/Email pages." },
  "email-classification-queue":{ what: "Inbound email phase 2: LLM tags each EmailMessage by intent (lead/customer/complaint/spam/other) + sentiment.",                                  stopped: "Inbound emails arrive untagged; CRM state analyzer can't progress contacts." },
  "email-reply-queue":         { what: "Drafts auto-reply for emails that match an auto-reply rule. Output is a draft EmailMessage requiring approval (unless auto-approved).",         stopped: "Auto-reply drafts stop. Inbound classification still works." },
  "email-send-queue":          { what: "SMTP outbound — transactional sends (auth, reset, approvals) + one-off campaign sends.",                                                          stopped: "No outbound email at all. Posting approval emails, password resets, campaign sends all halt." },
  "email-campaign-queue":      { what: "Bulk campaign dispatch. Reads EmailCampaign + ContactList, fans out per-recipient sends with per-batch pacing.",                                  stopped: "Campaigns get stuck in 'sending'. Transactional sends are unaffected (they use email-send-queue separately)." },
  "seo-brief-queue":           { what: "LLM-drafts an SEO brief (target keyword, intent, outline) from a topic + brand voice.",                                                          stopped: "SEO articles can't progress past 'pending-brief'." },
  "seo-article-queue":         { what: "LLM-writes a long-form SEO article from the brief.",                                                                                              stopped: "Approved briefs sit unwritten. Doesn't affect short-form posts." },
  "seo-faq-queue":             { what: "LLM-drafts FAQ answers for a topic (RAG-style, uses brand profile + cached knowledge).",                                                          stopped: "FAQ blocks won't refresh. Existing FAQs keep displaying." },
  "seo-keyword-research-queue":{ what: "Free-path keyword research (Google autocomplete + LLM expansion). No paid SerpAPI.",                                                              stopped: "Keyword research page returns no new suggestions. Manual entry still works." },
  "seo-publish-queue":         { what: "Fans an article out through configured CMS connectors (WordPress / Ghost / generic webhook). Each adapter is independent.",                       stopped: "Articles approved for publish stay in 'ready'. Doesn't affect drafting." },
  "chat-ingestion-queue":      { what: "Inbound chat message pipeline phase 1: parse + dedupe + persist as ChatMessage.",                                                                 stopped: "New chat messages don't land. Existing conversations still readable." },
  "chat-classification-queue": { what: "LLM tags a new chat message + decides whether to emit a draft reply request.",                                                                    stopped: "New messages arrive untagged. Auto-draft replies stop." },
  "chat-reply-queue":          { what: "Drafts a chat reply using brand voice + KB context (keyword-overlap retrieval). Output is a draft for human approval.",                           stopped: "Auto-draft replies stop. Operators can still type replies by hand." },
  "chat-send-queue":           { what: "Per-channel delivery. Webhook channels HMAC-sign + POST to channel.config.webhookUrl; widget/api channels flip status to 'sent'.",                stopped: "Replies queued up; operator can still respond from the inbox UI directly." },
  "chat-kb-index-queue":       { what: "Chunks + embeds knowledge-base articles for the chat-reply processor's retrieval step.",                                                          stopped: "Newly added KB articles aren't searchable. Old KB still retrievable." },
  "newsletter-generate-queue": { what: "LLM-drafts the next newsletter issue: pulls recent posts/articles/faqs, drafts the issue body, creates an EmailCampaign + notifies for approval.", stopped: "Newsletter issues stop drafting. Scheduled sends already approved still fire from email-send." },
  "trial-reminder-queue":      { what: "Sends the templated trial-expiry reminder email at 7d/3d/1d/0d thresholds and writes a TRIAL_REMINDER Notification for the bell UI.",            stopped: "Users stop receiving trial reminders. Trial expiry gate (mid-session) still works." },
  "crm-state-analyze-queue":   { what: "LLM summarises a contact's CRM state after every inbound classification; sets nextActionAt and state taxonomy.",                                 stopped: "Contacts' kanban state stops updating. Per-state action schedulers go quiet." },
  "crm-customer-action-queue": { what: "Fires per-state automation for a customer (check-in / retention / escalate).",                                                                    stopped: "Customer follow-ups stop. State analyzer still runs upstream." },
  "crm-prospect-action-queue": { what: "Fires per-state automation for a prospect (intro / nurturing / qualifying ask).",                                                                 stopped: "Prospect cadence pauses. State analyzer still runs." },
  "alert-notify-queue":        { what: "Per-company alert dispatch. Reads CompanyAlertConfig + env webhooks; fans out to Slack / Teams / Email per channel config.",                     stopped: "Operational alerts pile up but never reach humans. Worker errors still go to logs." },
  "perfectionist-queue":       { what: "Always-on polish stage between any artifact's draft step and its next-stage emission: de-AI regex pass + brand-voice LLM polish + optional image regen.", stopped: "Drafts skip polish — output reads more AI-flavoured. Pipeline keeps flowing." },
  "artifact-image-regen-queue":{ what: "Generic image regeneration for non-Post artifacts (SeoArticle, NewsletterIssue) with a brand-tuned prompt.",                                      stopped: "Non-Post imagery falls back to whatever was first generated. Post images unaffected." },
};

const SCHEDULER_INFO = {
  "token-refresh": {
    what: "Hourly DB scan for SocialAccounts whose tokens expire in <24h or are in error state. Enqueues a token-refresh job per account.",
    stopped: "Tokens won't be proactively refreshed. They'll only refresh on a posting failure (reactive)."
  },
  "monthly-content-planner": {
    what: "Every 6h, scans brands that don't already have an idea this calendar month and emits IDEAS_REQUESTED with a 30-day plan.",
    stopped: "New brands won't get a monthly content plan. dailyContentScheduler still acts as a fallback."
  },
  "daily-content": {
    what: "Hourly fallback. Catches brands that don't have a fresh idea today and emits an IDEAS_REQUESTED for them.",
    stopped: "Brands missing from the monthly plan won't backfill. Brands with a monthly plan are unaffected."
  },
  "due-post": {
    what: "Every 1 minute. Finds posts whose scheduled_at has elapsed and emits POST_APPROVED so social-posting picks them up.",
    stopped: "Scheduled posts never fire on time. Manual approval from the Kanban still works (fires immediately)."
  },
  "brand-enrichment": {
    what: "Hourly stale-check. Scans BrandSettings whose brandProfile.enrichedAt is missing or older than 7 days and emits BRAND_ENRICHMENT_REQUESTED for each (deduped per day). Event-driven enrichments from the app — brand create, settings update, social connect — fire independently and aren't blocked.",
    stopped: "Stale BrandProfiles won't auto-refresh. Event-driven re-enrichments from the app still work."
  },
  "engagement-refresh": {
    what: "Daily DB scan. For each Post published in the last 14 days whose analytics.lastUpdated is stale, emits ENGAGEMENT_REFRESH_REQUESTED so the worker re-fetches likes/comments/shares from each platform.",
    stopped: "Engagement counts stop updating; Post Analytics charts go stale until you manually emit the event."
  },
  "comment-refresh": {
    what: "Daily. For each Post published in the last 14 days, emits COMMENT_REFRESH_REQUESTED so the worker pulls actual comment bodies + commenter profiles from the platform.",
    stopped: "Comment bodies stop arriving. Sentiment classification has no new inputs. Top engagers list goes stale."
  },
  "queue-janitor":              { what: "Every 10 min: sweeps phantom-failed jobs from every queue's failed bucket. Without this, accumulated phantoms eventually trip the 100%-failure-rate recovery loop and stop the workers.", stopped: "Phantoms accumulate. Eventually the health check trips and queues auto-pause." },
  "email-inbox-poller":         { what: "Polls each EmailAccount's IMAP inbox on its configured cadence. Emits EMAIL_INGESTION_REQUESTED per account whose lastSyncAt is stale.",                                stopped: "Inbound email stops landing in the CRM/Email pages until the next manual trigger." },
  "email-campaign-dispatcher":  { what: "Claims due EmailCampaign rows (status='scheduled', scheduled_at<=now) and dispatches their queued recipients in batches.",                                              stopped: "Scheduled campaigns sit at the start time without sending. Transactional sends unaffected." },
  "newsletter":                 { what: "Hourly tick. Emits NEWSLETTER_GENERATE_REQUESTED for any NewsletterSchedule whose nextDueAt has elapsed.",                                                              stopped: "Recurring newsletters stop drafting new issues. One-off campaigns unaffected." },
  "trial-reminder":             { what: "Daily. Scans users approaching expiration (7/3/1/0d). Emits TRIAL_REMINDER_REQUESTED per threshold per user (exactly once via outbox dedupeKey).",                       stopped: "Trial reminder emails stop. Trial expiry gate (mid-session enforce) still works." },
  "crm-customer":               { what: "Hourly. Scans customer contacts whose nextActionAt has elapsed and emits per-state action requests (check-in / retention / escalate).",                                  stopped: "Customer follow-ups stop. CRM kanban states stop progressing on schedule." },
  "crm-prospect":               { what: "Same as crm-customer but for prospects (intro / nurturing / qualifying ask).",                                                                                          stopped: "Prospect cadence pauses. Manual moves on the kanban still work." },
  "deliverability-watcher":     { what: "Hourly. Recomputes per-account bounce/complaint/sent rolling counters. Auto-pauses mailboxes above 5% bounce / 0.1% complaint (50-sample floor).",                       stopped: "Bad mailboxes won't auto-pause. Manual pause from the Email page still works." },
  "llm-budget-ticker":          { what: "Every minute. Re-aggregates today's / month's aiCalls.usd across every entity into LlmBudget counters that the cost gate reads.",                                       stopped: "Cost gate decisions go stale (1-min lag becomes hours). Spend caps may be exceeded between recalcs." },
  "llm-provider-balance-pinger":{ what: "Every 30 min. Hits Anthropic + OpenAI admin/usage APIs and upserts what the providers say we actually spent. No-ops without admin keys.",                                stopped: "Cost dashboard loses the 'provider truth' overlay. Internal accounting still works." },
  "llm-projection-ticker":      { what: "Every 5 min. Persists live $/token rates + day/week/month/year projections + variance into LlmCostSnapshot for the projection chart.",                                   stopped: "Projection chart freezes. Spot prices keep being recorded on each LLM call." },
};

// Tiny info icon with a tooltip for queue/scheduler descriptions.
function InfoChip({ what, stopped }) {
  if (!what && !stopped) return null;
  const body = (
    <div style={{ maxWidth: 320, fontSize: 12, lineHeight: 1.5 }}>
      {what && <div><b>What it does:</b><br />{what}</div>}
      {stopped && <div style={{ marginTop: 8 }}><b>If stopped:</b><br />{stopped}</div>}
    </div>
  );
  return (
    <Tooltip title={body} placement="right">
      <span className="wh-info" aria-label="info">i</span>
    </Tooltip>
  );
}

// Tiny button used in row action cells.
function Btn({ onClick, danger, primary, disabled, children }) {
  const cls = [
    "wh-btn",
    danger && "wh-btn-danger",
    primary && "wh-btn-primary",
    disabled && "wh-btn-disabled"
  ].filter(Boolean).join(" ");
  return (
    <button className={cls} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

export default function WorkerHealth() {
  const dispatch = useDispatch();
  const userInfo = useSelector((s) => s.user.userInfo) || {};
  const isSuperAdmin = !!userInfo.superAdmin;
  // Company admins (and anyone non-super) only get the scoped clean —
  // pause/resume/empty/scheduler controls are hidden for them since
  // those affect every other company's data.
  const cleanFn = isSuperAdmin ? cleanQueue : cleanQueueByBrand;
  const cleanLabel = isSuperAdmin ? "Clean failed" : "Clean my failed";
  const cleanDoneLabel = isSuperAdmin ? "Clean done" : "Clean my done";

  const [health, setHealth] = useState(null);
  const [throughput, setThroughput] = useState(null);
  const [outbox, setOutbox] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [busy, setBusy] = useState({}); // per-action key → bool

  const refresh = useCallback(async () => {
    try {
      const [h, t, o] = await Promise.all([
        getWorkerHealth().catch((e) => ({ __error: e })),
        getWorkerThroughputSummary().catch(() => null),
        getWorkerOutboxStats().catch(() => null)
      ]);
      if (h?.__error) {
        setError(
          h.__error?.message ||
            "Worker unreachable. Check WORKER_BASE_URL / WORKER_DASHBOARD_API_KEY env."
        );
      } else {
        setError(null);
        setHealth(h);
      }
      setThroughput(t);
      setOutbox(o);
      setUpdatedAt(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(setSidebarStatus(false));
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [dispatch, refresh]);

  // Run a control action with busy tracking + toast + auto-refresh.
  const runAction = async (key, fn, label) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await fn();
      message.success(`${label} done`);
      await refresh();
    } catch (e) {
      message.error(`${label} failed: ${e?.error || e?.message || "unknown"}`);
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  // Bulk: drain a particular state across every queue. SuperAdmin gets
  // the global cleanQueue; everyone else gets the company-scoped variant
  // so they only nuke their own company's jobs.
  const cleanAll = async (status) => {
    const names = Object.keys(health?.queues || {});
    if (!names.length) return;
    setBusy((b) => ({ ...b, [`all:${status}`]: true }));
    const fn = isSuperAdmin ? cleanQueue : cleanQueueByBrand;
    const scope = isSuperAdmin ? "all queues" : "your company's jobs across all queues";
    const hide = message.loading(`Cleaning ${status} on ${scope}…`, 0);
    try {
      const results = await Promise.allSettled(names.map((n) => fn(n, status)));
      const failures = results.filter((r) => r.status === "rejected");
      hide();
      if (failures.length) {
        message.warning(`Cleaned ${results.length - failures.length}/${results.length} — ${failures.length} failed`);
      } else {
        message.success(`Cleaned ${status} on ${scope} (${results.length} queues)`);
      }
      await refresh();
    } catch (e) {
      hide();
      message.error(`clean-all failed: ${e?.error || e?.message || "unknown"}`);
    } finally {
      setBusy((b) => ({ ...b, [`all:${status}`]: false }));
    }
  };

  const queues = health?.queues || {};
  const workers = health?.workers || {};
  const schedulers = health?.schedulers || {};
  const system = health?.system || {};
  const queueCount = Object.keys(queues).length;
  const totalCompleted = Object.values(queues).reduce((s, q) => s + (q?.completed || 0), 0);
  const totalFailed = Object.values(queues).reduce((s, q) => s + (q?.failed || 0), 0);

  return (
    <MDBBox className="worker-health">
      <div className="wh-header">
        <h2>Worker health</h2>
        <div className="wh-meta">
          {updatedAt && <span>updated {updatedAt.toLocaleTimeString()}</span>}
          <Popconfirm
            title={isSuperAdmin ? "Clean failed jobs across ALL queues?" : "Clean YOUR company's failed jobs across all queues?"}
            description={isSuperAdmin
              ? `Drops every failed job in ${Object.keys(health?.queues || {}).length} queues across every company. Waiting/active are preserved.`
              : `Drops only failed jobs that belong to your company's brands.`}
            okText={isSuperAdmin ? "Clean failed (all)" : "Clean my failed"}
            cancelText="Cancel"
            onConfirm={() => cleanAll("failed")}
          >
            <button className="wh-refresh wh-refresh-danger" disabled={busy["all:failed"]}>
              {isSuperAdmin ? "Clean failed (all)" : "Clean my failed (all queues)"}
            </button>
          </Popconfirm>
          {isSuperAdmin && (
            <Popconfirm
              title="EMPTY ALL queues?"
              description="Drops failed, completed, waiting, active and delayed across every company. Use only when the system is wedged."
              okText="Empty all"
              cancelText="Cancel"
              okType="danger"
              onConfirm={() => cleanAll("all")}
            >
              <button className="wh-refresh wh-refresh-danger" disabled={busy["all:all"]}>
                Empty all
              </button>
            </Popconfirm>
          )}
          <button onClick={refresh} className="wh-refresh">Refresh</button>
        </div>
      </div>

      {loading && <div className="wh-loading">Loading…</div>}
      {error && <div className="wh-error">{error}</div>}

      {!loading && !error && health && (
        <>
          <div className="wh-tiles">
            <StatTile
              label="Status"
              value={<><StatusDot ok={totalFailed === 0} warn={totalFailed > 0} /> {totalFailed > 0 ? "degraded" : "healthy"}</>}
              sub={`uptime ${fmtSecs(system.uptime || 0)}`}
            />
            <StatTile label="Queues" value={queueCount} sub="bullmq" />
            <StatTile
              label="Schedulers"
              value={Object.keys(schedulers).length}
              sub={`${Object.values(schedulers).filter((s) => s?.running).length} running`}
            />
            <StatTile
              label="Outbox pending"
              value={outbox?.pending ?? "—"}
              sub={`${outbox?.failed ?? 0} failed / ${outbox?.published ?? 0} published`}
            />
            <StatTile
              label="Completed (lifetime)"
              value={totalCompleted}
              sub={`${totalFailed} failed total`}
            />
            <StatTile
              label="System"
              value={`${system.cpus || "?"} CPUs`}
              sub={`mem ${system.memory?.usedPercent ?? "?"}% of ${system.memory?.total ? fmtBytes(system.memory.total) : "?"}`}
            />
          </div>

          <h3>Queues</h3>
          <table className="wh-table">
            <thead>
              <tr>
                <th>Queue</th>
                <th>Workers</th>
                <th>State</th>
                <th>W</th>
                <th>A</th>
                <th>D</th>
                <th>C</th>
                <th>F</th>
                <th>Throughput</th>
                <th className="wh-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(queues).map(([name, q]) => {
                const w = workers[name] || {};
                const tp = throughput?.[name]?.current;
                const paused = !!q?.paused;
                const k = (op) => `q:${name}:${op}`;
                return (
                  <tr key={name}>
                    <td>
                      <StatusDot ok={(q?.failed || 0) === 0 && !paused} warn={!!q?.failed} /> {name}
                      <InfoChip {...(QUEUE_INFO[name] || {})} />
                      {paused && <span className="wh-pill wh-pill-warn">paused</span>}
                    </td>
                    <td>{w.workerCount ?? 0}/{w.maxWorkers ?? 0}{w.totalConcurrency ? ` (×${w.totalConcurrency})` : ""}</td>
                    <td>{w.healthStatus || "—"}</td>
                    <td>{q?.waiting ?? 0}</td>
                    <td>{q?.active ?? 0}</td>
                    <td>{q?.delayed ?? 0}</td>
                    <td>{q?.completed ?? 0}</td>
                    <td className={q?.failed ? "wh-bad" : ""}>{q?.failed ?? 0}</td>
                    <td>
                      {tp ? `${tp.throughput?.toFixed?.(2) || 0}/s · ${tp.successRate ?? 0}%` : "—"}
                    </td>
                    <td className="wh-actions">
                      {/* Pause/Resume affect every company's jobs — superAdmin only. */}
                      {isSuperAdmin && (
                        paused ? (
                          <Btn primary disabled={busy[k("resume")]} onClick={() => runAction(k("resume"), () => resumeQueue(name), `Resume ${name}`)}>Resume</Btn>
                        ) : (
                          <Btn disabled={busy[k("pause")]} onClick={() => runAction(k("pause"), () => pauseQueue(name), `Pause ${name}`)}>Pause</Btn>
                        )
                      )}
                      <Btn disabled={busy[k("cf")] || (q?.failed || 0) === 0} onClick={() => runAction(k("cf"), () => cleanFn(name, "failed"), `${cleanLabel} on ${name}`)}>{cleanLabel}</Btn>
                      <Btn disabled={busy[k("cc")] || (q?.completed || 0) === 0} onClick={() => runAction(k("cc"), () => cleanFn(name, "completed"), `${cleanDoneLabel} on ${name}`)}>{cleanDoneLabel}</Btn>
                      {/* Empty drops EVERY job — superAdmin only. */}
                      {isSuperAdmin && (
                        <Popconfirm
                          title="Drop ALL jobs in this queue?"
                          description="Wipes failed/completed/waiting/active/delayed across every company. Use only when wedged."
                          okText="Empty"
                          cancelText="Cancel"
                          onConfirm={() => runAction(k("ob"), () => obliterateQueue(name), `Empty ${name}`)}
                        >
                          <Btn danger disabled={busy[k("ob")]}>Empty</Btn>
                        </Popconfirm>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <h3>Schedulers</h3>
          {Object.keys(schedulers).length === 0 ? (
            <div className="wh-loading">No schedulers reported.</div>
          ) : (
            <table className="wh-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Running</th>
                  <th>Last tick</th>
                  <th>Next tick</th>
                  <th>Interval</th>
                  <th>Last error</th>
                  <th className="wh-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(schedulers).map(([name, s]) => {
                  const k = (op) => `s:${name}:${op}`;
                  return (
                    <tr key={name}>
                      <td>
                        <StatusDot ok={!!s?.running} warn={!s?.running} /> {name}
                        <InfoChip {...(SCHEDULER_INFO[name] || {})} />
                      </td>
                      <td>{s?.running ? "yes" : "no"}</td>
                      <td>{fmtTime(s?.lastRunAt)}</td>
                      <td>{fmtTime(s?.nextRunAt)}</td>
                      <td>{fmtInterval(s?.intervalMs)}</td>
                      <td className={s?.lastError ? "wh-bad" : ""}>{(s?.lastError || "").slice(0, 90) || "—"}</td>
                      <td className="wh-actions">
                        {/* Schedulers run for every company — superAdmin only. */}
                        {isSuperAdmin ? (
                          <>
                            <Btn primary disabled={busy[k("run")]} onClick={() => runAction(k("run"), () => runSchedulerNow(name), `Run ${name}`)}>Run now</Btn>
                            {s?.running ? (
                              <Btn disabled={busy[k("pause")]} onClick={() => runAction(k("pause"), () => pauseScheduler(name), `Pause ${name}`)}>Pause</Btn>
                            ) : (
                              <Btn disabled={busy[k("resume")]} onClick={() => runAction(k("resume"), () => resumeScheduler(name), `Resume ${name}`)}>Resume</Btn>
                            )}
                          </>
                        ) : (
                          <span className="wh-pill" style={{ color: "#9ca3af" }}>read-only</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </MDBBox>
  );
}
